<script setup lang="ts">
  defineOptions({ name: 'CPModal', inheritAttrs: false })

  // External library imports

  import { HLModal } from '@platform-ui/highrise'

  // Type imports

  // Props definitions
  const props = defineProps({
    id: {
      type: String,
      required: true,
    },
    show: {
      type: Boolean,
      required: false,
      default: false,
    },
    width: {
      type: Number,
      required: false,
      default: undefined,
    },
    height: {
      type: Number,
      required: false,
      default: undefined,
    },
    closeOnEsc: {
      type: Boolean,
      required: false,
      default: true,
    },
    maskClosable: {
      type: Boolean,
      required: false,
      default: true,
    },
    zIndex: {
      type: Number,
      required: false,
      default: undefined,
    },
    showHeaderIcon: {
      type: Boolean,
      default: false,
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    'update:show': [show: any]
    onEsc: []
    onClose: []
    onOpen: []
  }>()

  // User-defined functions
  const handleUpdateShow = (show: any) => {
    emit('update:show', show)
  }

  const handleEsc = () => {
    emit('onEsc')
  }

  const handleClose = () => {
    emit('onClose')
  }

  const handleOpen = () => {
    emit('onOpen')
  }
</script>

<template>
  <HLModal
    v-bind="$attrs"
    :id="props.id"
    :show="props.show"
    :width="props.width"
    :height="props.height"
    :close-on-esc="props.closeOnEsc"
    :show-header-icon="props.showHeaderIcon"
    :mask-closable="props.maskClosable"
    :z-index="props.zIndex"
    @update:show="handleUpdateShow"
    @on-esc="handleEsc"
    @on-close="handleClose"
    @on-open="handleOpen"
  >
    <template v-if="$slots.header" #header>
      <slot name="header" />
    </template>
    <template v-if="$slots.footer" #footer>
      <slot name="footer" />
    </template>
    <slot />
  </HLModal>
</template>

<!--
  HighRise dialog overrides — unscoped because HLModal teleports its
  surface (and the destructive button restoration block targets buttons
  contextually nested under .hr-dialog) into document.body. <style scoped>
  and :deep() cannot reach teleported nodes.

  Important detail: setting `--n-color: var(--cp-background-500)` with
  !important on .hr-dialog cascades into ALL descendants. That overwrites
  every nested button's own inline --n-color, making primary/destructive
  buttons render background-on-background. The nested .hr-dialog .hr-button
  rules below restore each semantic colour explicitly so primary and error
  buttons remain visible inside dialogs.

  HighRise injects --n-* custom properties as inline styles via its JS
  theme engine, so !important is required to beat inline specificity.
-->
<style>
  /* Modal / dialog surface — uses the page background, not the elevated surface */
  .hr-dialog {
    --n-color: var(--cp-background-500) !important;
    --n-text-color: var(--cp-brand-font) !important;
  }

  /*
   * Re-establish button fill colours inside modals.
   *
   * CSS custom properties with !important cascade into ALL descendants.
   * .hr-dialog sets --n-color to the background token which overwrites the
   * button's own inline --n-color, making primary buttons invisible (they render
   * background-colour-on-background-colour at 45% opacity in disabled state).
   * Restore each semantic colour explicitly here.
   */
  .hr-dialog .hr-button.hr-button--primary {
    --n-color: var(--cp-primary-500) !important;
    --n-color-hover: var(--cp-primary-600) !important;
    --n-color-pressed: var(--cp-primary-700) !important;
    --n-color-focus: var(--cp-primary-500) !important;
    --n-text-color: var(--cp-text-on-primary) !important;
    --n-text-color-hover: var(--cp-text-on-primary) !important;
    --n-text-color-pressed: var(--cp-text-on-primary) !important;
    --n-text-color-focus: var(--cp-text-on-primary) !important;
    --n-border: 1px solid var(--cp-primary-500) !important;
    --n-border-hover: 1px solid var(--cp-primary-600) !important;
    --n-border-pressed: 1px solid var(--cp-primary-700) !important;
    --n-border-focus: 1px solid var(--cp-primary-500) !important;
  }

  /* Error-coloured primary button inside dialog */
  .hr-dialog .hr-button.hr-button--primary.hr-button--color-error {
    --n-color: var(--cp-error-500) !important;
    --n-color-hover: var(--cp-error-600) !important;
    --n-color-pressed: var(--cp-error-700) !important;
    --n-text-color: var(--cp-text-on-error) !important;
    --n-border: 1px solid var(--cp-error-500) !important;
  }
</style>
